import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Chip,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Drawer,
  Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { PortfolioDeleteDocuments, PortfolioGetFolderStructure, PortfolioRenameFolder } from "../../util/API/portfolio";
import BulkUploadDrawer from "./BulkUploadDrawer";
import CustomAlert from "../../components/UI/CustomAlert/CustomAlert";
import mixpanel from "../../util/utilfunctions/mixpanel";

// ── Helper: format bytes ──
const formatBytes = (bytes) => {
  if (!bytes || bytes === "0") return "0 B";
  const b = parseInt(bytes, 10);
  if (b === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${parseFloat((b / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

// ── Helper: get file extension badge color ──
const extColor = (name = "") => {
  const ext = name.split(".").pop()?.toLowerCase();
  const map = { pdf: "#e53935", doc: "#1565c0", docx: "#1565c0", jfif: "#6a1b9a", jpg: "#6a1b9a", png: "#2e7d32", xlsx: "#2e7d32", xls: "#2e7d32" };
  return map[ext] || "#455a64";
};

const FileExplorer = ({ onBack }) => {
  const configVariables = useSelector((state) => state.config?.baseConfig);
  const userSecurityRoles = JSON.parse(sessionStorage.getItem("user"))?.payload?.body?.Securityuserrole;
  const userId = JSON.parse(sessionStorage.getItem("user"))?.userid || "";

  const userRole = useMemo(() => {
    const userRoles = Array.isArray(userSecurityRoles) ? userSecurityRoles : [userSecurityRoles];
    const parseIds = (ids) => ids?.toString().split(",").map((id) => Number(id.trim())) || [];
    const adminRoles = parseIds(configVariables?.portfolioAdminRoleIds);
    const delegateRoles = parseIds(configVariables?.delegateRoleIds);
    if (userRoles.some((r) => adminRoles.includes(Number(r?.roleid)))) return "admin";
    if (userRoles.some((r) => delegateRoles.includes(Number(r?.roleid)))) return "delegate";
    return "investor";
  }, [userSecurityRoles, configVariables]);

  const [folderTree, setFolderTree] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [openNodes, setOpenNodes] = useState({});
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [previewFile, setPreviewFile] = useState(null); // side panel file
  const [fullScreenFile, setFullScreenFile] = useState(null); // full screen preview
  const [alertNew, setAlertNew] = useState({ showAlert: false, severity: "", header: "", message: "", onConfirm: null, alertType: "" });
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [newName, setNewName] = useState("");

  const PROTECTED_ROOT_FOLDERS = ["Global Repository", "documents", "funds", "investors"];

  // ── Flat list of ALL nodes (files + folders) for search ──
  const { allFiles, allFolders } = useMemo(() => {
    const files = [];
    const folders = [];
    const walk = (nodes, path = "") => {
      nodes.forEach((n) => {
        if (n.type === "file") {
          files.push({ ...n, _folderPath: path });
        } else {
          // push the folder itself with its path
          folders.push({ ...n, _folderPath: path });
          if (n.children) walk(n.children, path ? `${path} / ${n.name}` : n.name);
        }
      });
    };
    walk(folderTree);
    return { allFiles: files, allFolders: folders };
  }, [folderTree]);

  // ── Search: match file name OR folder path; also match folders by name ──
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return { files: [], folders: [] };
    const q = searchQuery.toLowerCase();

    // Files: match on file name OR the folder path it lives in
    const matchedFiles = allFiles.filter((f) =>
      f.name.toLowerCase().includes(q) ||
      f._folderPath.toLowerCase().includes(q)
    );

    // Folders: match on folder name OR its path
    const matchedFolders = allFolders.filter((f) =>
      f.name.toLowerCase().includes(q) ||
      f._folderPath.toLowerCase().includes(q)
    );

    return { files: matchedFiles, folders: matchedFolders };
  }, [searchQuery, allFiles, allFolders]);

  const getFolderStructure = async () => {
    setIsLoading(true);
    const roleToBeSend = userRole === "admin" ? "admin" : "investor";
    try {
      const response = await PortfolioGetFolderStructure(userId, roleToBeSend);
      const items = response?.data?.fileList?.item;
      if (!items) return;
      const itemsArray = Array.isArray(items) ? items : [items];
      const root = {};
      const getOrCreateFolder = (parentObj, segName, fullPath) => {
        if (!parentObj[segName]) {
          parentObj[segName] = { __meta: { id: fullPath, name: segName, type: "folder", children: [], __subfolders: {} } };
        }
        return parentObj[segName];
      };
      itemsArray.forEach((item) => {
        const { s3Key, accessUrl, lastModified, fileSize, can_delete } = item;
        if (!s3Key) return;
        const segments = s3Key.split("/").filter(Boolean);
        if (segments.length === 0) return;
        const lastSeg = segments[segments.length - 1];
        const isFile = lastSeg.includes(".");
        const folderSegs = isFile ? segments.slice(0, -1) : segments;
        if (folderSegs.length === 0) return;
        let currentObj = root;
        let pathSoFar = "";
        folderSegs.forEach((seg) => {
          pathSoFar = pathSoFar ? `${pathSoFar}/${seg}` : seg;
          const node = getOrCreateFolder(currentObj, seg, pathSoFar);
          currentObj = node.__meta.__subfolders;
        });
        if (isFile) {
          let cur = root;
          let deepestMeta = null;
          folderSegs.forEach((seg) => { deepestMeta = cur[seg].__meta; cur = deepestMeta.__subfolders; });
          if (deepestMeta) {
            deepestMeta.children.push({ id: s3Key, name: lastSeg, type: "file", date: lastModified?.split(" ")[0], accessUrl: accessUrl?.replace(/&amp;/g, "&"), fileSize, can_delete });
          }
        }
      });
      const buildTree = (obj) => Object.keys(obj).filter((k) => k !== "__meta").map((key) => {
        const meta = obj[key].__meta;
        return { id: meta.id, name: meta.name, type: "folder", children: [...buildTree(meta.__subfolders), ...meta.children] };
      });
      const tree = buildTree(root);
      setOpenNodes({});
      setFolderTree(tree);
    } catch (error) {
      console.log("getFolderStructure error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { if (userId && userRole) getFolderStructure(); }, [userId, userRole]);
  useEffect(() => { if (folderTree.length > 0 && !selectedFolder) setSelectedFolder(folderTree[0]); }, [folderTree]);

  const handleToggle = (id) => setOpenNodes((prev) => ({ ...prev, [id]: !prev[id] }));

  const isInsideProtectedBranch = (nodeId) => {
    const fundsBranch = folderTree.find((c) => c.id === "funds");
    if (!fundsBranch) return false;
    const findInTree = (root) => { if (root.id === nodeId) return true; if (root.children) return root.children.some((child) => findInTree(child)); return false; };
    return findInTree(fundsBranch);
  };

  const handleDeleteConfirmation = async (itemData) => {
    setAlertNew({ ...alertNew, showAlert: true, message: "Are you sure you want to delete this file?", severity: "confirm", header: "Please Confirm", alertType: "confirm", onConfirm: () => handleDeleteDoc(itemData) });
  };

  const handleDeleteDoc = async (itemData) => {
    const fileKey = itemData?.id;
    if (!fileKey) return;
    try {
      const response = await PortfolioDeleteDocuments({ file_key: fileKey });
      if (response?.data?.header?.callStatus === "1") {
        setAlertNew((prev) => ({ ...prev, showAlert: false }));
        setSelectedFolder(null);
        if (previewFile?.id === itemData.id) setPreviewFile(null);
        await getFolderStructure();
        setAlertNew({ showAlert: true, severity: "success", header: "Success", message: "File deleted successfully", alertType: "info" });
      } else {
        setAlertNew({ showAlert: true, severity: "error", header: "Error", message: response?.data?.header?.msg || "Failed to delete file", alertType: "info" });
      }
    } catch (error) {
      setAlertNew({ showAlert: true, severity: "error", header: "Error", message: "An internal error occurred while deleting.", alertType: "info" });
    }
  };

  const handleRenameFolder = async (node) => {
    if (!newName.trim() || newName === node.name) { setEditingFolderId(null); return; }
    const pathSegments = node.id.split("/");
    pathSegments[pathSegments.length - 1] = newName.trim();
    const newPath = pathSegments.join("/");
    try {
      const response = await PortfolioRenameFolder(node.id, newPath);
      if (response?.data?.header?.callStatus === "1") {
        setAlertNew({ showAlert: true, severity: "success", header: "Success", message: "Folder renamed successfully", alertType: "info" });
        setEditingFolderId(null);
        getFolderStructure();
      } else throw new Error(response?.data?.header?.msg || "Rename failed");
    } catch (error) {
      setAlertNew({ showAlert: true, severity: "error", header: "Error", message: error.message, alertType: "info" });
    }
  };

  const handleDownload = async (item) => {
    mixpanel.track("File Downloaded", { fileName: item.name, fileType: item.name?.split(".").pop(), fileId: item.id, folderId: selectedFolder?.id });
    try {
      const response = await fetch(item.accessUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = item.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      window.open(item.accessUrl, "_blank");
    }
  };

  // ── Sidebar Tree ──
  const RenderTree = ({ node, level = 0 }) => {
    const isFolder = node.type === "folder";
    const isOpen = openNodes[node.id];
    const isEditing = editingFolderId === node.id;
    const isProtected = PROTECTED_ROOT_FOLDERS.includes(node.name);

    return (
      <React.Fragment>
        <ListItemButton
          onClick={() => { if (isFolder && !isEditing) { handleToggle(node.id); setSelectedFolder(node); } }}
          selected={selectedFolder?.id === node.id}
          sx={{ pl: level * 2 + 2, py: 0.5, "&:hover .folder-edit-icon": { opacity: isProtected ? 0 : 1 } }}
        >
          <ListItemIcon sx={{ minWidth: 30 }}>
            {isFolder ? (isOpen ? <ExpandMore fontSize="small" /> : <ChevronRightIcon fontSize="small" />) : <Box sx={{ width: 20 }} />}
            <FolderIcon fontSize="small" sx={{ color: "#ffca28", ml: 0.5 }} />
          </ListItemIcon>
          {isEditing ? (
            <Box display="flex" alignItems="center" sx={{ flexGrow: 1, gap: 1 }}>
              <TextField variant="standard" size="small" autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === "Enter") handleRenameFolder(node); if (e.key === "Escape") setEditingFolderId(null); }}
                sx={{ flexGrow: 1, "& .MuiInput-root": { fontSize: "0.8rem" } }} />
              <Box display="flex">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRenameFolder(node); }}><CheckIcon sx={{ fontSize: "1.1rem", color: "green" }} /></IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingFolderId(null); setNewName(""); }}><CloseIcon sx={{ fontSize: "1.1rem", color: "red" }} /></IconButton>
              </Box>
            </Box>
          ) : (
            <ListItemText primary={node.name} primaryTypographyProps={{ fontSize: "0.8rem", fontWeight: selectedFolder?.id === node.id ? 700 : 400 }} />
          )}
          {isFolder && !isEditing && userRole === "admin" && !isProtected && (
            <IconButton size="small" className="folder-edit-icon"
              onClick={(e) => { e.stopPropagation(); setEditingFolderId(node.id); setNewName(node.name); }}
              sx={{ opacity: 0, transition: "opacity 0.2s", padding: "2px" }}>
              <EditIcon sx={{ fontSize: "0.9rem", color: "#00365A" }} />
            </IconButton>
          )}
        </ListItemButton>
        {isFolder && node.children && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {node.children.map((child) => <RenderTree key={child.id} node={child} level={level + 1} />)}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  // ── File Row used in both folder view & search results ──
  const FileRow = ({ item, index, totalCount, parentFolder }) => (
    <React.Fragment key={item.id}>
      <ListItemButton
        onClick={() => item.type === "folder" && setSelectedFolder(item)}
        sx={{
          py: 1.5,
          px: 2,
          transition: "background 0.15s",
          "&:hover": { bgcolor: "#f0f7ff" },
          ...(previewFile?.id === item.id && { bgcolor: "#e8f4fd", borderLeft: "3px solid #002e4c" }),
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          {item.type === "folder"
            ? <FolderIcon sx={{ color: "#ffca28", fontSize: "1.4rem" }} />
            : (
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <InsertDriveFileIcon sx={{ color: "#90a4ae", fontSize: "1.4rem" }} />
                <Box sx={{ position: "absolute", bottom: -2, right: -4, bgcolor: extColor(item.name), color: "#fff", fontSize: "0.45rem", fontWeight: 700, px: "2px", borderRadius: "2px", lineHeight: 1.4, textTransform: "uppercase" }}>
                  {item.name.split(".").pop()?.slice(0, 4)}
                </Box>
              </Box>
            )}
        </ListItemIcon>
        <ListItemText
          primary={<Typography variant="body2" fontWeight={500} sx={{ color: "#1a2e44", lineHeight: 1.3 }} noWrap>{item.name}</Typography>}
          secondary={
            item.type === "folder"
              ? <Typography variant="caption" color="textSecondary">{item.children?.length || 0} item{item.children?.length !== 1 ? "s" : ""}</Typography>
              : <Typography variant="caption" color="textSecondary">Added: {item.date} {item._folderPath ? `· ${item._folderPath}` : ""}</Typography>
          }
        />
        {item.type === "file" && (
          <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
            {/* Eye / Preview */}
            <Tooltip title="Preview" placement="top">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setPreviewFile(previewFile?.id === item.id ? null : item); }}
                sx={{ color: previewFile?.id === item.id ? "#002e4c" : "#90a4ae", bgcolor: previewFile?.id === item.id ? "#e3eaf3" : "transparent", borderRadius: 1, "&:hover": { bgcolor: "#e3eaf3", color: "#002e4c" } }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Download */}
            <Tooltip title="Download" placement="top">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownload(item); }}
                sx={{ color: "#1976d2", borderRadius: 1, "&:hover": { bgcolor: "#e3eaf3" } }}>
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Delete */}
            {item.can_delete !== "0" && (userRole === "admin" || !isInsideProtectedBranch(selectedFolder?.id)) && (
              <Tooltip title="Delete" placement="top">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteConfirmation(item); }}
                  sx={{ color: "#e53935", borderRadius: 1, "&:hover": { bgcolor: "#fdecea" } }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </ListItemButton>
      {index < totalCount - 1 && <Divider sx={{ mx: 2 }} />}
    </React.Fragment>
  );

  // ── Side Detail Panel (Frame 2) ──
  const PreviewPanel = ({ file, onClose }) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    const isImage = ["jpg", "jpeg", "png", "gif", "webp", "jfif"].includes(ext);

    return (
      <Box sx={{ width: 300, flexShrink: 0, height: "100%", display: "flex", flexDirection: "column", bgcolor: "#fff", borderLeft: "1px solid #e0e0e0" }}>
        {/* Panel Header */}
        <Box sx={{ px: 2, py: 1.5, bgcolor: "#002e4c", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#fff", fontSize: "0.82rem" }}>
            Document Details
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: "#fff", p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
          {/* File icon / thumbnail */}
          <Box sx={{ bgcolor: "#f5f7fa", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 140 }}>
            {isImage ? (
              <Box component="img" src={file.accessUrl} alt={file.name}
                sx={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain" }}
                onError={(e) => { e.target.style.display = "none"; }} />
            ) : (
              <Box sx={{ textAlign: "center" }}>
                <InsertDriveFileIcon sx={{ fontSize: "3.5rem", color: extColor(file.name), opacity: 0.85 }} />
                <Typography variant="caption" sx={{ display: "block", color: "#888", mt: 0.5, textTransform: "uppercase", fontWeight: 700, letterSpacing: 1 }}>
                  {ext} file
                </Typography>
              </Box>
            )}
          </Box>

          {/* Meta */}
          <Box sx={{ px: 2.5, py: 2, flex: 1 }}>
            <Typography fontWeight={700} sx={{ color: "#1a2e44", wordBreak: "break-all", fontSize: "0.85rem", lineHeight: 1.4, mb: 2 }}>
              {file.name}
            </Typography>

            {[
              { icon: <CalendarTodayIcon sx={{ fontSize: "0.85rem", color: "#90a4ae" }} />, label: "Added", value: file.date || "—" },
              { icon: <DataUsageIcon sx={{ fontSize: "0.85rem", color: "#90a4ae" }} />, label: "File Size", value: formatBytes(file.fileSize) },
              { icon: <FolderOpenIcon sx={{ fontSize: "0.85rem", color: "#90a4ae" }} />, label: "Format", value: ext?.toUpperCase() },
            ].map(({ icon, label, value }) => (
              <Box key={label} sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, mb: 1.5 }}>
                <Box sx={{ mt: "2px" }}>{icon}</Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#90a4ae", fontSize: "0.67rem", textTransform: "uppercase", letterSpacing: 0.5, display: "block" }}>{label}</Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#1a2e44", fontWeight: 500 }}>{value}</Typography>
                </Box>
              </Box>
            ))}

            <Chip
              label={file.can_delete === "0" ? "Protected" : "Deletable"} size="small"
              sx={{ mt: 1, fontSize: "0.67rem", fontWeight: 600, bgcolor: file.can_delete === "0" ? "#fff3e0" : "#e8f5e9", color: file.can_delete === "0" ? "#e65100" : "#2e7d32", border: `1px solid ${file.can_delete === "0" ? "#ffcc80" : "#a5d6a7"}` }}
            />
          </Box>

          <Divider />

          {/* Actions */}
          <Box sx={{ px: 2, py: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {/* Open Document → goes to full-screen preview */}
            <Button
              fullWidth variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => { setFullScreenFile(file); setPreviewFile(null); }}
              sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.78rem", bgcolor: "#002e4c", borderRadius: 1.5, "&:hover": { bgcolor: "#004a7a" } }}
            >
              Open Document
            </Button>
            <Button
              fullWidth variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={() => handleDownload(file)}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.78rem", borderColor: "#002e4c", color: "#002e4c", borderRadius: 1.5, "&:hover": { bgcolor: "#f0f4f8" } }}
            >
              Download
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  // ── Full-Screen Document Preview (Frame 3) ──
  const FullScreenPreview = ({ file, onBack }) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    const isImage = ["jpg", "jpeg", "png", "gif", "webp", "jfif"].includes(ext);
    const isPdf = ext === "pdf";
    const isDoc = ["doc", "docx"].includes(ext);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#fff" }}>
        {/* Top bar */}
        <Box sx={{ px: 2, py: 1.5, bgcolor: "#002e4c", display: "flex", alignItems: "center", gap: 1.5, borderBottom: "1px solid #001e33" }}>
          <IconButton size="small" onClick={onBack} sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.1)", borderRadius: 1, "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          {/* Breadcrumb */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", cursor: "pointer", "&:hover": { color: "#fff" }, whiteSpace: "nowrap" }} onClick={onBack}>
              Documents
            </Typography>
            <ChevronRightIcon sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.4)" }} />
            {selectedFolder && (
              <>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", cursor: "pointer", "&:hover": { color: "#fff" }, whiteSpace: "nowrap" }} onClick={onBack}>
                  {selectedFolder.name}
                </Typography>
                <ChevronRightIcon sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.4)" }} />
              </>
            )}
            <Typography variant="caption" sx={{ color: "#fff", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {file.name}
            </Typography>
          </Box>
          {/* Download in top bar */}
          <Button
            size="small" variant="outlined"
            startIcon={<FileDownloadIcon sx={{ fontSize: "0.9rem !important" }} />}
            onClick={() => handleDownload(file)}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem", borderColor: "rgba(255,255,255,0.4)", color: "#fff", borderRadius: 1.5, whiteSpace: "nowrap", "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.1)" } }}
          >
            Download
          </Button>
        </Box>

        {/* Body: viewer left + details right */}
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* ── Document Viewer ── */}
          <Box sx={{ flex: 1, bgcolor: "#f0f2f5", display: "flex", alignItems: "center", justifyContent: "center", overflow: "auto", p: 2 }}>
            {isPdf ? (
              <Box
                component="iframe"
                src={file.accessUrl}
                title={file.name}
                sx={{ width: "100%", height: "100%", minHeight: "60vh", border: "none", borderRadius: 1, boxShadow: "0 2px 12px rgba(0,0,0,0.12)" }}
              />
            ) : isImage ? (
              <Box component="img" src={file.accessUrl} alt={file.name}
                sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 1, boxShadow: "0 2px 12px rgba(0,0,0,0.12)" }} />
            ) : (
              <Box sx={{ textAlign: "center", p: 4 }}>
                <InsertDriveFileIcon sx={{ fontSize: "5rem", color: extColor(file.name), opacity: 0.7, mb: 2 }} />
                <Typography variant="body1" fontWeight={600} color="#1a2e44" mb={0.5}>{file.name}</Typography>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  This file type cannot be previewed in the browser.
                </Typography>
                <Button variant="contained" startIcon={<FileDownloadIcon />} onClick={() => handleDownload(file)}
                  sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#002e4c", borderRadius: 1.5, "&:hover": { bgcolor: "#004a7a" } }}>
                  Download to View
                </Button>
              </Box>
            )}
          </Box>

          {/* ── Right Details Panel ── */}
          <Box sx={{ width: 260, flexShrink: 0, borderLeft: "1px solid #e0e0e0", display: "flex", flexDirection: "column", bgcolor: "#fff", overflowY: "auto" }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #eee" }}>
              <Typography variant="subtitle2" fontWeight={700} color="#002e4c" fontSize="0.82rem">
                File Details
              </Typography>
            </Box>

            <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              {/* File icon */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5, px: 1.5, bgcolor: "#f5f7fa", borderRadius: 1.5 }}>
                <InsertDriveFileIcon sx={{ fontSize: "2rem", color: extColor(file.name) }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} color="#1a2e44" sx={{ wordBreak: "break-all", fontSize: "0.78rem", lineHeight: 1.3 }}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>
                    {ext} · {formatBytes(file.fileSize)}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              {[
                { label: "File Name", value: file.name },
                { label: "Format", value: ext?.toUpperCase() },
                { label: "File Size", value: formatBytes(file.fileSize) },
                { label: "Added", value: file.date || "—" },
              ].map(({ label, value }) => (
                <Box key={label}>
                  <Typography variant="caption" sx={{ color: "#90a4ae", fontSize: "0.67rem", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#1a2e44", fontWeight: 500, mt: 0.2, wordBreak: "break-all" }}>
                    {value}
                  </Typography>
                </Box>
              ))}

              <Chip
                label={file.can_delete === "0" ? "Protected" : "Deletable"} size="small"
                sx={{ alignSelf: "flex-start", fontSize: "0.67rem", fontWeight: 600, bgcolor: file.can_delete === "0" ? "#fff3e0" : "#e8f5e9", color: file.can_delete === "0" ? "#e65100" : "#2e7d32", border: `1px solid ${file.can_delete === "0" ? "#ffcc80" : "#a5d6a7"}` }}
              />

              <Divider />

              <Button
                fullWidth variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={() => handleDownload(file)}
                sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.78rem", bgcolor: "#002e4c", borderRadius: 1.5, "&:hover": { bgcolor: "#004a7a" } }}
              >
                Download
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const isSearchMode = !!searchQuery.trim();
  const totalSearchCount = isSearchMode ? searchResults.files.length + searchResults.folders.length : 0;

  // ── Full-screen mode: replace entire component body ──
  if (fullScreenFile) {
    return (
      <>
        <Box sx={{ borderRadius: 2, border: "1px solid #eee", bgcolor: "#fff", height: "calc(75vh + 57px)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <FullScreenPreview
            file={fullScreenFile}
            onBack={() => { setFullScreenFile(null); }}
          />
        </Box>
        <CustomAlert
          severity={alertNew.severity} show={alertNew.showAlert} header={alertNew.header} message={alertNew.message}
          onClick={() => setAlertNew({ ...alertNew, showAlert: false })}
          alertType={alertNew.alertType} confirmButton={alertNew.onConfirm}
          autoclose={alertNew.severity === "error" ? false : true}
          confirmButtonText={"Yes"} cancelButtonText={"No"}
        />
      </>
    );
  }

  return (
    <>
      <Box sx={{ borderRadius: 2, border: "1px solid #eee", bgcolor: "#fff" }}>
        {/* ── Header ── */}
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} borderBottom="1px solid #eee">
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={onBack} size="small"><ArrowBackIcon fontSize="small" /></IconButton>
            <Typography variant="subtitle1" fontWeight="bold">Document Hub</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Button variant="contained" size="small" startIcon={<FileUploadIcon sx={{ fontSize: "1rem !important" }} />} onClick={() => setBulkUploadOpen(true)}
              sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.78rem", bgcolor: "#002e4c", px: 2, py: 0.75, borderRadius: 1.5, "&:hover": { bgcolor: "#004a7a" } }}>
              Bulk Upload
            </Button>
            <Chip
              icon={userRole === "admin" ? <AdminPanelSettingsIcon sx={{ fontSize: "1rem !important" }} /> : userRole === "delegate" ? <SupervisorAccountIcon sx={{ fontSize: "1rem !important" }} /> : <PersonIcon sx={{ fontSize: "1rem !important" }} />}
              label={userRole.toUpperCase()}
              color={userRole === "admin" ? "primary" : userRole === "delegate" ? "secondary" : "default"}
              size="small"
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", height: "75vh", overflow: "hidden" }}>
          {/* ── Sidebar ── */}
          <Box sx={{ width: 260, flexShrink: 0, borderRight: "1px solid #eee", bgcolor: "#fcfcfc", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Search bar inside sidebar */}
            <Box sx={{ px: 1.5, py: 1.2, borderBottom: "1px solid #eee", bgcolor: "#fff" }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search documents…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: "1rem", color: "#90a4ae" }} /></InputAdornment>,
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ p: 0.3 }}><CloseIcon sx={{ fontSize: "0.85rem" }} /></IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: { fontSize: "0.8rem", borderRadius: 1.5, bgcolor: "#f5f7fa", "& fieldset": { borderColor: "#e0e0e0" }, "&:hover fieldset": { borderColor: "#b0bec5" }, "&.Mui-focused fieldset": { borderColor: "#002e4c" } },
                }}
              />
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto" }}>
              <List disablePadding sx={{ py: 1 }}>
                {isLoading ? (
                  <Box p={3} textAlign="center"><Typography variant="caption" color="textSecondary">Loading…</Typography></Box>
                ) : folderTree.length > 0 ? (
                  folderTree.map((node) => <RenderTree key={node.id} node={node} />)
                ) : (
                  <Box p={3} textAlign="center"><Typography variant="caption" color="textSecondary">No documents found.</Typography></Box>
                )}
              </List>
            </Box>
          </Box>

          {/* ── Main Content ── */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
            {isSearchMode ? (
              <>
                {/* Search header */}
                <Box mb={2} display="flex" alignItems="center" gap={1}>
                  <SearchIcon sx={{ color: "#90a4ae", fontSize: "1.1rem" }} />
                  <Typography variant="h6" fontWeight={800} color="#002e4c">Search Results</Typography>
                  <Chip
                    label={`${totalSearchCount} found`}
                    size="small"
                    sx={{ fontSize: "0.7rem", bgcolor: "#e8f4fd", color: "#002e4c", fontWeight: 700 }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>
                    for "<strong>{searchQuery}</strong>"
                  </Typography>
                </Box>

                {totalSearchCount === 0 ? (
                  <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                    <Box p={5} textAlign="center">
                      <SearchIcon sx={{ fontSize: "2.5rem", color: "#cfd8dc", mb: 1 }} />
                      <Typography color="textSecondary" variant="body2">
                        No folders or documents match "<strong>{searchQuery}</strong>"
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 0.5 }}>
                        Try searching by folder name (e.g. "Holt") or part of a file name
                      </Typography>
                    </Box>
                  </Paper>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    {/* ── Matching Folders ── */}
                    {searchResults.folders.length > 0 && (
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <FolderIcon sx={{ fontSize: "0.95rem", color: "#ffca28" }} />
                          <Typography variant="caption" fontWeight={700} sx={{ color: "#555", textTransform: "uppercase", letterSpacing: 0.8, fontSize: "0.7rem" }}>
                            Folders ({searchResults.folders.length})
                          </Typography>
                        </Box>
                        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                          <List disablePadding>
                            {searchResults.folders.map((folder, i) => (
                              <React.Fragment key={folder.id}>
                                <ListItemButton
                                  onClick={() => { setSelectedFolder(folder); setSearchQuery(""); }}
                                  sx={{ py: 1.5, px: 2, "&:hover": { bgcolor: "#f0f7ff" } }}
                                >
                                  <ListItemIcon sx={{ minWidth: 38 }}>
                                    <FolderIcon sx={{ color: "#ffca28", fontSize: "1.3rem" }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Typography variant="body2" fontWeight={600} sx={{ color: "#1a2e44" }}>
                                        {folder.name}
                                      </Typography>
                                    }
                                    secondary={
                                      <Typography variant="caption" color="textSecondary">
                                        {folder._folderPath || "Root"} · {folder.children?.filter(c => c.type === "file").length || 0} file{folder.children?.filter(c => c.type === "file").length !== 1 ? "s" : ""}
                                      </Typography>
                                    }
                                  />
                                  <ChevronRightIcon sx={{ color: "#b0bec5", fontSize: "1rem" }} />
                                </ListItemButton>
                                {i < searchResults.folders.length - 1 && <Divider sx={{ mx: 2 }} />}
                              </React.Fragment>
                            ))}
                          </List>
                        </Paper>
                      </Box>
                    )}

                    {/* ── Matching Files ── */}
                    {searchResults.files.length > 0 && (
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <InsertDriveFileIcon sx={{ fontSize: "0.95rem", color: "#90a4ae" }} />
                          <Typography variant="caption" fontWeight={700} sx={{ color: "#555", textTransform: "uppercase", letterSpacing: 0.8, fontSize: "0.7rem" }}>
                            Files ({searchResults.files.length})
                          </Typography>
                        </Box>
                        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                          <List disablePadding>
                            {searchResults.files.map((item, i) => (
                              <FileRow key={item.id} item={item} index={i} totalCount={searchResults.files.length} />
                            ))}
                          </List>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            ) : selectedFolder ? (
              <>
                <Box mb={2.5}>
                  <Typography variant="h5" fontWeight={900} color="#002e4c">{selectedFolder.name}</Typography>
                  <Typography variant="caption" color="textSecondary">{selectedFolder.children?.length || 0} item{selectedFolder.children?.length !== 1 ? "s" : ""}</Typography>
                </Box>
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  <List disablePadding>
                    {selectedFolder.children?.length > 0
                      ? selectedFolder.children.map((item, i) => <FileRow key={item.id} item={item} index={i} totalCount={selectedFolder.children.length} />)
                      : <Box p={6} textAlign="center"><Typography color="textSecondary" variant="body2">This folder is empty</Typography></Box>}
                  </List>
                </Paper>
              </>
            ) : (
              <Box display="flex" height="100%" alignItems="center" justifyContent="center">
                <Typography color="textSecondary">Select a directory from the sidebar</Typography>
              </Box>
            )}
          </Box>

          {/* ── Preview Panel ── */}
          {previewFile && <PreviewPanel file={previewFile} onClose={() => setPreviewFile(null)} />}
        </Box>

        <CustomAlert
          severity={alertNew.severity}
          show={alertNew.showAlert}
          header={alertNew.header}
          message={alertNew.message}
          onClick={() => setAlertNew({ ...alertNew, showAlert: false })}
          alertType={alertNew.alertType}
          confirmButton={alertNew.onConfirm}
          autoclose={alertNew.severity === "error" ? false : true}
          confirmButtonText={"Yes"}
          cancelButtonText={"No"}
        />
      </Box>

      <BulkUploadDrawer
        userRole={userRole}
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onUploadComplete={() => { setBulkUploadOpen(false); setSelectedFolder(null); setFolderTree([]); getFolderStructure(); }}
      />
    </>
  );
};

export default FileExplorer;
